import { computed, ref, watch, type Ref } from 'vue';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import { useShipmentEditHydration } from '@/composables/useShipmentEditHydration';
import { useShipmentEditCapabilitiesContext } from '@/composables/useShipmentEditCapabilitiesContext';
import { useShipmentEditActions } from '@/composables/useShipmentEditActions';
import { useShipmentFormStopLocks } from '@/composables/useShipmentFormStopLocks';
import { useShipmentFormDeviceLookup } from '@/composables/useShipmentFormDeviceLookup';
import { useShipmentFormDeviceSelection } from '@/composables/useShipmentFormDeviceSelection';
import type { ShipmentFormStopsPort } from '@/composables/shipment-form-stops-port';
import { emptyGeneralErrors } from '@/composables/useShipmentFormValidation';
import { useDevice } from '@/composables/useDevice';

export function useShipmentEditFlow(options: {
  shipmentId: Ref<number>;
  stopsPort: Ref<ShipmentFormStopsPort | null>;
}) {
  const vuexStore = useStore();
  const accountId = computed(
    () => vuexStore.getters['auth/accountId'] as number,
  );

  const generalErrors = ref(emptyGeneralErrors());
  const { selectedDevicesPickerValue } = useShipmentFormDeviceSelection();

  const capsContext = useShipmentEditCapabilitiesContext({
    accountId,
    shipmentId: options.shipmentId,
  });

  const { devices: devicesMap } = storeToRefs(useDevice());
  const { deviceLabelForId, assignedDevices } = useShipmentFormDeviceLookup({
    devicesMap,
    deviceIds: capsContext.deviceIds,
  });

  const stopLocks = useShipmentFormStopLocks(capsContext.capabilities);
  const formStore = useShipmentFormStore();

  function initDeviceValueFromShipment(ids: readonly number[]) {
    const uniqueIds = [...new Set(ids.filter((id) => id != null && id > 0))];
    if (uniqueIds.length === 0) {
      formStore.clearSelectedDevices();
      return;
    }
    formStore.setSelectedDevices(
      uniqueIds.map((id) => ({ id, label: deviceLabelForId(id) })),
    );
  }

  const { hydrated, routesHydrationReady } = useShipmentEditHydration({
    shipmentId: options.shipmentId,
    shipmentQuery: capsContext.shipmentQuery,
    capabilities: capsContext.capabilities,
    accountId,
    initDeviceValueFromShipment,
  });

  const actions = useShipmentEditActions({
    accountId,
    shipmentId: options.shipmentId,
    stopsPort: options.stopsPort,
    capabilities: capsContext.capabilities,
    routingStrategy: capsContext.routingStrategy,
    canEditTracker: capsContext.canEditTracker,
    generalErrors,
  });

  const {
    generalName,
    generalCarrier,
    generalNotes,
    generalPlannedRange,
    selectedDevices,
  } = storeToRefs(formStore);

  watch(
    () => devicesMap.value.size,
    () => {
      if (!hydrated.value || !capsContext.shipment.value) return;
      if (selectedDevices.value.length > 0) return;
      initDeviceValueFromShipment(capsContext.shipment.value.device_ids ?? []);
    },
  );

  return {
    shipment: capsContext.shipment,
    shipmentQuery: capsContext.shipmentQuery,
    capabilities: capsContext.capabilities,
    routingStrategy: capsContext.routingStrategy,
    primaryTrackerPosition: capsContext.primaryTrackerPosition,
    trackerPositions: capsContext.trackerPositions,
    trackerLoading: capsContext.trackerLoading,
    routesHydrationReady,
    canEditTracker: capsContext.canEditTracker,
    assignedDevices,
    selectedDevicesPickerValue,
    generalErrors,
    saveBusy: actions.saveBusy,
    publishBusy: actions.publishBusy,
    getStopDisabledFields: stopLocks.getStopDisabledFields,
    getStopLockReason: stopLocks.getStopLockReason,
    getStopLockBadgeLabel: stopLocks.getStopLockBadgeLabel,
    hideWaypointControls: stopLocks.hideWaypointControls,
    save: actions.save,
    publish: actions.publish,
    cancel: actions.cancel,
    displayStatus: capsContext.displayStatus,
    hydrated,
    generalName,
    generalCarrier,
    generalNotes,
    generalPlannedRange,
  };
}
